import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-hot-toast';
import axiosInstance from '../axiosConfig';

// Define template data types
export interface TemplateUIData {
  name: string;
  title?: string;
  description?: string;
  prompt?: string;
  language?: string;
  template_type?: string | null;
  is_public: boolean;
}

interface TemplateUIState {
  isLoading: boolean;
  error: string | null;
  currentTemplate: any | null;
}

const initialState: TemplateUIState = {
  isLoading: false,
  error: null,
  currentTemplate: null,
};

// Async thunk to generate a UI template
export const generateUITemplate = createAsyncThunk(
  'templateUI/generateTemplate',
  async (
    { projectIdOrSlug, templateData }: { projectIdOrSlug: string | number, templateData: TemplateUIData },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(`/ui-template/${projectIdOrSlug}`, templateData);
      return response.data;
    } catch (error: any) {
      console.error('Error generating UI template:', error);
      const errorMessage = error.response?.data?.message || 'Failed to generate template';
      return rejectWithValue(errorMessage);
    }
  }
);

// Template UI slice
const templateUISlice = createSlice({
  name: 'templateUI',
  initialState,
  reducers: {
    clearTemplateState: (state) => {
      state.currentTemplate = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Generate UI template
      .addCase(generateUITemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(generateUITemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTemplate = action.payload;
        state.error = null;
      })
      .addCase(generateUITemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        toast.error(state.error || 'Failed to generate template');
      });
  },
});

export const { clearTemplateState } = templateUISlice.actions;

export default templateUISlice.reducer; 